import type {
  GetListingV3PublicResponse,
  TaxV3,
  ListingKind,
} from '@kouto/types';

// Matches the Listing type from @kouto/cart-prices
type CartCompatibleTax = {
  name: string;
  includedInPrice?: boolean;
  id?: string;
  order?: number;
  showInAdvertisedPrice?: boolean;
  appliesTo?: string[];
} & ({ percentage: number } | { amount: number });

type CartCompatibleResourceGroup = {
  id: string;
  taxes?: CartCompatibleTax[];
};

export type CartCompatibleListing = {
  taxes: CartCompatibleTax[] | null;
  defaultMaxParticipantCount?: number | null;
  resourceGroups?: CartCompatibleResourceGroup[] | null;
  kind: ListingKind;
  map?: {
    id: string;
    uri: {
      original: string;
      '384w': string;
      '480w': string;
      '640w': string;
      '750w': string;
      '1080w': string;
    };
  } | null;
};

/**
 * Adapts V3 listing to cart-compatible format for cart calculations
 */

export const adaptListingV3ForCart = (
  listingV3: GetListingV3PublicResponse,
): CartCompatibleListing => {
  return {
    taxes: adaptTaxesV3ForCart(listingV3.taxes),
    defaultMaxParticipantCount: listingV3.maxParticipantCount ?? null,
    resourceGroups:
      listingV3.resourceGroups?.map((rg) => {
        // Get all taxes from all experiences in the resource group
        const allTaxes = rg.experiences.flatMap((exp) => exp.taxes);

        // Deduplicate by tax ID
        const uniqueTaxes = Array.from(
          new Map(allTaxes.map((tax: TaxV3) => [tax.id, tax])).values(),
        );

        return {
          id: rg.id,
          taxes: adaptTaxesV3ForCart(uniqueTaxes) ?? undefined,
        };
      }) ?? null,
    kind: listingV3.kind,
    map: null,
  };
};

const adaptTaxesV3ForCart = (taxesV3: TaxV3[]): CartCompatibleTax[] | null => {
  if (!taxesV3 || taxesV3.length === 0) return null;

  return taxesV3
    .filter((tax) => {
      // Filter out taxes that have neither percentage nor amount
      return tax.percentage != null || tax.amount != null;
    })
    .map((tax) => {
      const base = {
        name: tax.name ?? '',
        includedInPrice: tax.includedInPrice,
        // Preserve compound tax fields
        id: tax.id,
        order: tax.order ?? undefined,
        showInAdvertisedPrice: tax.showInAdvertisedPrice ?? undefined,
        appliesTo: tax.appliesTo ?? undefined,
      };

      // Create discriminated union: either percentage OR amount
      if (tax.percentage != null) {
        return { ...base, percentage: tax.percentage };
      }
      if (tax.amount != null) {
        return { ...base, amount: tax.amount };
      }
      // Fallback (should never reach here due to filter above)
      return { ...base, amount: 0 };
    });
};

/**
 * Adapts a record of V3 listings for cart calculations
 */
export const adaptListingsV3ForCart = (
  listingsV3: Record<string, GetListingV3PublicResponse>,
): Record<string, CartCompatibleListing> => {
  return Object.entries(listingsV3).reduce((acc, [key, listing]) => {
    acc[key] = adaptListingV3ForCart(listing);
    return acc;
  }, {} as Record<string, CartCompatibleListing>);
};

/**
 * Adapts cart items with V3 addOn taxes to cart-compatible format
 */
export const adaptCartItemsForCart = <
  T extends {
    addOns?: Array<{ taxes?: TaxV3[] | CartCompatibleTax[] | null }>;
  },
>(
  cartItems: Record<string, T>,
): Record<string, T> => {
  return Object.entries(cartItems).reduce((acc, [key, cartItem]) => {
    acc[key] = {
      ...cartItem,
      addOns: cartItem.addOns?.map((addOn) => {
        // Check if taxes need conversion (V3 taxes have 'id' property, CartCompatible don't)
        const needsConversion =
          Array.isArray(addOn.taxes) &&
          addOn.taxes.length > 0 &&
          'id' in addOn.taxes[0];

        return {
          ...addOn,
          taxes: needsConversion
            ? adaptTaxesV3ForCart(addOn.taxes as TaxV3[])
            : addOn.taxes,
        };
      }),
    };
    return acc;
  }, {} as Record<string, T>);
};

/**
 * Adapts a single cart item's addOn taxes to cart-compatible format
 */
export const adaptCartItemForCart = <
  T extends {
    addOns?: Array<{ taxes?: TaxV3[] | CartCompatibleTax[] | null }>;
  },
>(
  cartItem: T,
): T & { addOns?: Array<{ taxes?: CartCompatibleTax[] | null }> } => {
  return {
    ...cartItem,
    addOns: cartItem.addOns?.map((addOn) => {
      // Check if taxes need conversion (V3 taxes have 'id' property, CartCompatible don't)
      const needsConversion =
        Array.isArray(addOn.taxes) &&
        addOn.taxes.length > 0 &&
        'id' in addOn.taxes[0];

      return {
        ...addOn,
        taxes: needsConversion
          ? adaptTaxesV3ForCart(addOn.taxes as TaxV3[])
          : (addOn.taxes as CartCompatibleTax[] | null),
      };
    }),
  } as T & { addOns?: Array<{ taxes?: CartCompatibleTax[] | null }> };
};
